<script lang="ts">
  /**
   * Settings screen (spec §12), sections in spec order: account + quota, appearance & language,
   * identities & signature, notifications, trusted senders, muted conversations, about.
   * The theme effect lives in the shell; this screen only calls `applyTheme()`.
   */
  import { untrack } from 'svelte';
  import { kvGet, kvSet } from '$lib/db';
  import { lang, setLang, t, type Lang } from '$lib/i18n';
  import { formatQuota, initialOf, quotaLevel } from '$lib/mail/format';
  import { applyTheme, readTheme, store } from '$lib/store';
  import { tooltip } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import { ADVANCE_CHOICES, LANGUAGE_LABEL, UNDO_CHOICES, compactIdentities, notifyHint, parseIdentities, quotaPercent, type Identity } from './_local';

  const SAVED_MS = 2000;

  let theme = $state<'dark' | 'light'>(readTheme());
  let identities = $state<Identity[]>([{ name: '', sig: '' }]);
  let saved = $state(false);
  let savedTimer: ReturnType<typeof setTimeout> | undefined;

  const quota = $derived(store.quota);
  const quotaKnown = $derived(quota !== null && quota.supported && quota.limit > 0);
  const quotaPct = $derived(quotaKnown && quota ? quotaPercent(quota.used, quota.limit) : 0);
  const quotaClass = $derived(quotaKnown && quota ? quotaLevel(quota.used, quota.limit) : 'ok');
  const quotaText = $derived(quotaKnown && quota ? t('quotaOf', { u: formatQuota(quota.used), l: formatQuota(quota.limit) }) : t('quotaNA'));

  const pushOn = $derived(store.pushOn);
  const hint = $derived(notifyHint({ perm: store.notifyPerm, canPush: store.canPush, needsInstall: store.needsInstall, on: pushOn }));

  // Mount work (spec §12 effects): quota, muted list, permission refresh + push status, identities.
  // `untrack` keeps the store reads inside these calls from re-triggering the effect.
  $effect(() => {
    untrack(() => {
      void store.loadQuota();
      void store.loadMuted();
      store.refreshNotifyPerm();
      void store.bootPush();
      void loadIdentities();
    });
    return () => clearTimeout(savedTimer);
  });

  function setTheme(next: 'dark' | 'light'): void {
    theme = next;
    applyTheme(next);
  }

  function chooseLang(next: Lang): void {
    if (next !== lang.current) setLang(next);
  }

  async function loadIdentities(): Promise<void> {
    try {
      const [raw, sig] = await Promise.all([kvGet('identities'), kvGet('signature')]);
      identities = parseIdentities(raw, sig);
    } catch {
      /* database unavailable: keep the blank row */
    }
  }

  function addIdentity(): void {
    identities.push({ name: '', sig: '' });
  }

  function removeIdentity(i: number): void {
    if (identities.length > 1) identities.splice(i, 1);
  }

  async function save(): Promise<void> {
    const keep = compactIdentities(identities);
    try {
      await kvSet('identities', JSON.stringify(keep));
      await kvSet('signature', keep[0]?.sig ?? '');
    } catch {
      /* IndexedDB refused the write: the button keeps saying `save`, nothing was persisted */
      return;
    }
    identities = keep;
    saved = true;
    clearTimeout(savedTimer);
    savedTimer = setTimeout(() => (saved = false), SAVED_MS);
  }
</script>

<div class="settings">
  <header class="bar">
    <button type="button" class="ghost" aria-label={t('back')} onclick={() => store.backToList()} use:tooltip>
      <span class="flip-ltr"><Icon name="back" size={20} /></span>
    </button>
    <h2>{t('settings')}</h2>
  </header>

  <div class="body">
    <section class="account">
      <h3>{t('account')}</h3>
      <div class="card">
        <div class="item">
          <span class="avatar" aria-hidden="true">{initialOf(null, store.user)}</span>
          <span class="val" dir="ltr">{store.user}</span>
        </div>
        <div class="item quota">
          <span class="label">{t('quota')}</span>
          <span class="muted quota-text">{quotaText}</span>
        </div>
        {#if quotaKnown}
          <div class="qbar" role="progressbar" aria-label={t('quota')} aria-valuemin="0" aria-valuemax="100" aria-valuenow={quotaPct}>
            <span class="qfill" class:warn={quotaClass === 'warn'} class:full={quotaClass === 'full'} style="width:{quotaPct}%"></span>
          </div>
        {/if}
        <button type="button" class="item danger" onclick={() => void store.logout()}>
          <Icon name="logout" size={18} />
          <span class="label">{t('logout')}</span>
        </button>
      </div>
    </section>

    <section class="appearance">
      <h3>{t('appearance')}</h3>
      <div class="card">
        <div class="item">
          <span class="label" id="set-theme">{t('theme')}</span>
          <span class="seg theme-seg" role="radiogroup" aria-labelledby="set-theme">
            <button type="button" role="radio" data-theme="light" aria-checked={theme === 'light'} class:on={theme === 'light'} onclick={() => setTheme('light')}>
              <Icon name="sun" size={16} />
              {t('light')}
            </button>
            <button type="button" role="radio" data-theme="dark" aria-checked={theme === 'dark'} class:on={theme === 'dark'} onclick={() => setTheme('dark')}>
              <Icon name="moon" size={16} />
              {t('darkM')}
            </button>
          </span>
        </div>
        <div class="item">
          <span class="label" id="set-undo">{t('undoWindow')}</span>
          <span class="seg undo-seg" role="radiogroup" aria-labelledby="set-undo">
            {#each UNDO_CHOICES as sec (sec)}
              <button type="button" role="radio" data-sec={sec} aria-checked={store.undoSec === sec} class:on={store.undoSec === sec} onclick={() => store.setUndoSec(sec)}>
                {sec === 0 ? t('undoOff') : sec}
              </button>
            {/each}
          </span>
        </div>
        <div class="item">
          <span class="label" id="set-adv">{t('autoAdvance')}</span>
          <span class="seg adv-seg" role="radiogroup" aria-labelledby="set-adv">
            {#each ADVANCE_CHOICES as c (c.value)}
              <button type="button" role="radio" data-adv={c.value} aria-checked={store.autoAdvance === c.value} class:on={store.autoAdvance === c.value} onclick={() => store.setAutoAdvance(c.value)}>
                {t(c.key)}
              </button>
            {/each}
          </span>
        </div>
        <div class="item">
          <span class="label" id="set-lang">{LANGUAGE_LABEL}</span>
          <span class="seg lang-seg" role="radiogroup" aria-labelledby="set-lang">
            <button type="button" role="radio" data-lang="ar" lang="ar" aria-checked={lang.current === 'ar'} class:on={lang.current === 'ar'} onclick={() => chooseLang('ar')}>العربية</button>
            <button type="button" role="radio" data-lang="en" lang="en" aria-checked={lang.current === 'en'} class:on={lang.current === 'en'} onclick={() => chooseLang('en')}>English</button>
          </span>
        </div>
      </div>
    </section>

    <section class="identities">
      <h3>{t('identities')}</h3>
      <div class="card pad">
        {#each identities as ident, i (i)}
          <div class="ident">
            <div class="ident-head">
              <input type="text" dir="auto" autocomplete="off" placeholder={t('identityName')} aria-label={t('identityName')} bind:value={ident.name} />
              {#if identities.length > 1}
                <button type="button" class="mini" aria-label={t('del')} onclick={() => removeIdentity(i)} use:tooltip>
                  <Icon name="x" size={16} />
                </button>
              {/if}
            </div>
            <textarea rows="2" placeholder={t('signatureHint')} aria-label={t('signature')} bind:value={ident.sig}></textarea>
          </div>
        {/each}
        <p class="hint">{t('identityHint')}</p>
        <div class="ident-foot">
          <button type="button" class="ghost-btn" onclick={addIdentity}>
            <Icon name="plus" size={16} />
            {t('addIdentity')}
          </button>
          <button type="button" class="save" onclick={() => void save()}>{saved ? t('saved') : t('save')}</button>
        </div>
      </div>
    </section>

    <section class="notifications">
      <h3>{t('notifications')}</h3>
      <div class="card" data-on={pushOn ? '1' : '0'}>
        <div class="item ntf-state" aria-live="polite">
          <span class="label">
            {#if pushOn}
              <Icon name="check" size={16} />
              {t('pushOnHere')}
            {:else}
              {t('notifyOffNow')}
            {/if}
          </span>
          {#if store.pushDevices > 0}
            <span class="muted">{t('pushDevices', { n: store.pushDevices })}</span>
          {/if}
        </div>
        <div class="item ntf-actions">
          {#if pushOn}
            <button type="button" class="ntf-test" disabled={store.pushBusy} onclick={() => void store.testPush()}>{t('pushTest')}</button>
            <button type="button" class="ghost danger-h ntf-off" aria-label={t('del')} disabled={store.pushBusy} onclick={() => void store.disableNotifications()} use:tooltip>
              <Icon name="trash" size={18} />
            </button>
          {:else}
            <button type="button" class="ntf-enable" disabled={store.pushBusy} onclick={() => void store.enableNotifications()}>{t('notifyEnable')}</button>
          {/if}
        </div>
        <div class="item">
          <small class="muted ntf-hint">{hint}</small>
        </div>
      </div>
    </section>

    <section class="trusted">
      <h3>{t('trustedSenders')}</h3>
      <div class="card">
        {#if !store.trustedSenders.length}
          <div class="item"><span class="muted">{t('trustedNone')}</span></div>
        {:else}
          {#each store.trustedSenders as s (s.id)}
            <div class="item trusted-row" data-id={s.id}>
              <span class="val" dir="ltr">{s.name}</span>
              <button type="button" class="ghost danger-h untrust" aria-label={t('del')} onclick={() => void store.untrustSender(s.id)} use:tooltip>
                <Icon name="trash" size={16} />
              </button>
            </div>
          {/each}
        {/if}
      </div>
    </section>

    <section class="muted-list">
      <h3>{t('mutedThreads')}</h3>
      <div class="card">
        {#if !store.mutedThreads.length}
          <div class="item"><span class="muted">{t('mutedNone')}</span></div>
        {:else}
          {#each store.mutedThreads as m (m.id)}
            <div class="item muted-row" data-id={m.id}>
              <span class="val" dir="auto">{m.subject || t('noSubject')}</span>
              <button type="button" class="ghost-btn unmute" onclick={() => void store.unmute(m.id)}>{t('unmuteBtn')}</button>
            </div>
          {/each}
        {/if}
      </div>
    </section>

    <section class="about">
      <h3>{t('about')}</h3>
      <div class="card">
        <div class="item">
          <img src="/img/logo.svg" alt="" width="28" height="28" />
          <span class="label">{t('appName')}</span>
          <span class="muted" dir="ltr">{t('version')} {__APP_VERSION__}</span>
        </div>
      </div>
    </section>
  </div>
</div>

<style>
  /* ===== component: svelte-13qe3dh (Settings) — copied verbatim from legacy-components.css ===== */
  .settings {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;
    min-height: 0;
    background: var(--surface-subtle)
  }

  .bar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    height: 56px;
    padding-inline: var(--space-3);
    background: var(--surface-card);
    border-bottom: 1px solid var(--border-subtle)
  }

  .bar h2 {
    font-size: var(--text-lg);
    color: var(--text-strong)
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ghost:hover {
    background: var(--surface-sunken)
  }

  .ghost:active {
    background: var(--mail-pressed)
  }

  .body {
    overflow-y: auto;
    min-height: 0;
    padding: var(--space-4);
    padding-bottom: calc(var(--space-16) + env(safe-area-inset-bottom));
    display: grid;
    gap: var(--space-6);
    align-content: start;
    max-width: 560px;
    width: 100%;
    margin-inline: auto
  }

  h3 {
    font-size: var(--text-sm);
    color: var(--text-muted);
    margin-bottom: var(--space-2);
    padding-inline: var(--space-2)
  }

  .card {
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-card);
    overflow: hidden
  }

  .card.pad {
    padding: var(--space-4);
    display: grid;
    gap: var(--space-3)
  }

  .item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4);
    width: 100%;
    text-align: start;
    color: var(--text-body)
  }

  .item+.item {
    border-top: 1px solid var(--border-subtle)
  }

  .item .label {
    flex: 1
  }

  .item .val {
    flex: 1;
    color: var(--text-strong);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
  }

  .muted {
    color: var(--text-faint);
    font-size: var(--text-sm);
    line-height: var(--leading-body)
  }

  button.item:hover {
    background: var(--surface-subtle)
  }

  .danger {
    color: var(--danger-500)
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-pill);
    background: var(--blue-100);
    color: var(--blue-700);
    display: grid;
    place-items: center;
    font-weight: var(--weight-semibold);
    flex: none
  }

  /* `html` is outside the component, so the ancestor must be :global or Svelte prunes the rule. */
  :global(html.tik-dark) .avatar {
    background: var(--blue-900);
    color: var(--blue-200)
  }

  .seg {
    display: inline-flex;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    overflow: hidden
  }

  .ntf-enable,
  .ntf-test {
    min-block-size: var(--control-lg);
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-control);
    border: 1px solid var(--border-default);
    background: var(--surface-card);
    color: var(--text-body);
    font: inherit;
    cursor: pointer
  }

  .ntf-enable {
    background: var(--blue-500);
    color: var(--text-on-accent);
    border-color: transparent;
    font-weight: var(--weight-semibold)
  }

  .ntf-enable:disabled {
    opacity: .6;
    cursor: default
  }

  .ntf-test:active {
    background: var(--mail-pressed)
  }

  .ntf-state {
    gap: var(--space-2)
  }

  .ntf-state .label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2)
  }

  .seg button {
    padding: var(--space-2) var(--space-3);
    min-block-size: var(--control-sm);
    font-size: var(--text-sm);
    color: var(--text-muted)
  }

  .seg button:active {
    background: var(--mail-pressed)
  }

  .seg button.on {
    background: var(--blue-500);
    color: var(--text-on-accent)
  }

  .seg button.on:active {
    background: var(--blue-600)
  }

  textarea {
    min-block-size: calc(var(--control-lg) * 2);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    padding: var(--space-3);
    background: var(--surface-card);
    color: var(--text-body);
    font: inherit;
    resize: vertical
  }

  textarea:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
    outline: none
  }

  .qbar {
    height: var(--space-2);
    background: var(--surface-sunken);
    margin: 0 var(--space-4) var(--space-4);
    border-radius: var(--radius-pill);
    overflow: hidden
  }

  .qfill {
    display: block;
    height: 100%;
    background: var(--blue-500);
    border-radius: var(--radius-pill);
    transition: width var(--duration-fast) var(--ease-standard)
  }

  .qfill.warn {
    background: var(--warning-500)
  }

  .qfill.full {
    background: var(--danger-500)
  }

  .ident {
    display: grid;
    gap: var(--space-2)
  }

  .ident+.ident {
    border-top: 1px dashed var(--border-subtle);
    padding-top: var(--space-3)
  }

  .ident-head {
    display: flex;
    gap: var(--space-2);
    align-items: center
  }

  .ident-head input {
    flex: 1;
    min-width: 0;
    min-block-size: var(--control-lg);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    padding: var(--space-2) var(--space-3);
    background: var(--surface-card);
    color: var(--text-body);
    font: inherit
  }

  .ident-head input:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
    outline: none
  }

  .mini {
    display: inline-grid;
    place-items: center;
    min-inline-size: var(--control-sm);
    min-block-size: var(--control-sm);
    padding: var(--space-2);
    border-radius: var(--radius-xs);
    color: var(--text-faint)
  }

  .mini:hover {
    background: var(--surface-sunken);
    color: var(--danger-500)
  }

  .mini:active {
    background: var(--mail-pressed);
    color: var(--danger-500)
  }

  .hint {
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: var(--leading-body)
  }

  .ident-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3)
  }

  .ghost-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-link);
    font-size: var(--text-sm);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-control)
  }

  .ghost-btn:hover {
    background: var(--surface-subtle)
  }

  .ghost-btn:active {
    background: var(--mail-pressed)
  }

  .save {
    justify-self: end;
    background: var(--blue-500);
    color: var(--text-on-accent);
    block-size: var(--control-lg);
    padding-inline: var(--space-5);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium)
  }

  .save:hover {
    background: var(--blue-600)
  }

  .save:active {
    transform: scale(.99)
  }

  /* App-shell rule the delete-labelled buttons rely on (was global in the bundle). */
  .danger-h:hover {
    color: var(--danger-500)
  }

  /* ===== appended global fixes (2026-09-04) that target Settings ===== */
  @media (pointer:fine) {
    .body {
      scrollbar-gutter: stable
    }
  }

  /* performance: no layout transition on the quota bar */
  .qfill {
    transition: none
  }

  /* one line + ellipsis without raising the bar's min-content */
  .bar h2 {
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow-wrap: anywhere
  }

  /* wide panes: real columns instead of a 560px ribbon */
  @media (min-width:1100px) {
    .body {
      max-width: 900px
    }

    .body>* {
      min-width: 0
    }
  }

  @media (min-width:1280px) {
    .body {
      max-width: 1160px;
      grid-template-columns: repeat(auto-fit, minmax(470px, 1fr));
      align-items: start;
      column-gap: var(--space-6)
    }

    .body>section {
      min-width: 0
    }
  }

  /* settings rows wrap on narrow screens instead of overflowing the card */
  @media (max-width:480px) {

    .body>*,
    .card {
      min-width: 0
    }

    .item {
      flex-wrap: wrap;
      row-gap: var(--space-2)
    }

    .item .label {
      flex: 1 1 auto;
      min-width: 0
    }

    .item .seg {
      flex: 1 1 100%;
      display: flex
    }

    .seg button {
      flex: 1 1 auto
    }
  }
</style>
